import type { CprBoundaryModel } from './cprBoundaryUtils';
import type {
  BoundaryFrame,
  BoundarySide,
  SolidLumenVolumeVoxelCollection,
  Vec3,
  VoxelIJKChange,
} from './markerLineTypes';
import {
  clampValue,
  findCenterRunInMaskRow,
  getChangedBounds,
  getVoxelKey,
  crossVec3,
  normalizeVec3,
} from './markerLineUtils';
import {
  getBoundaryRowAxis,
  getImageSpacingMm,
  worldPointToVoxelIJK,
} from './cprVoxelUtils';

type CollectorsParams = {
  getBoundaryFrame: () => BoundaryFrame | null;
  getLabelData: () => any;
  getBoundaryModel: () => CprBoundaryModel | null;
  getManualModelAddedVoxelMap: () => Map<string, VoxelIJKChange>;
};

export const createCprLumenVolumeVoxelCollectors = ({
  getBoundaryFrame,
  getLabelData,
  getBoundaryModel,
  getManualModelAddedVoxelMap,
}: CollectorsParams) => {
  const findClosestBoundaryRowForWorld = (world: Vec3, frame: BoundaryFrame) => {
    if (!frame.rows.length) return -1;
    const stride = Math.max(1, Math.floor(frame.rows.length / 128));
    let bestRow = 0;
    let bestD2 = Number.POSITIVE_INFINITY;

    for (let row = 0; row < frame.rows.length; row += stride) {
      const c = frame.rows[row].center;
      const dx = world[0] - c[0];
      const dy = world[1] - c[1];
      const dz = world[2] - c[2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestRow = row;
      }
    }

    const refine = Math.max(8, stride * 3);
    const start = Math.max(0, bestRow - refine);
    const end = Math.min(frame.rows.length - 1, bestRow + refine);
    for (let row = start; row <= end; row += 1) {
      const c = frame.rows[row].center;
      const dx = world[0] - c[0];
      const dy = world[1] - c[1];
      const dz = world[2] - c[2];
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestRow = row;
      }
    }
    return bestRow;
  };

  const collectManualAddedVoxelsOutsideCurrentBoundary = ({
    mask,
    changed,
    side,
    method,
    paddingPx = 10,
    keepMarginPx = 0.35,
  }: {
    mask: Uint8Array;
    changed: Iterable<number>;
    side?: BoundarySide;
    method: string;
    paddingPx?: number;
    keepMarginPx?: number;
  }): SolidLumenVolumeVoxelCollection => {
    const startedAt = performance.now();
    const frame = getBoundaryFrame();
    const labelData = getLabelData();
    const indexToWorld = labelData?.indexToWorld?.bind(labelData) as
      | ((ijk: [number, number, number]) => Vec3)
      | undefined;
    const fallback: SolidLumenVolumeVoxelCollection = {
      voxelIJK: [],
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D: 0,
      changedSeedCount2D: 0,
      config: {
        method,
        radiusUMm: 0,
        radiusVMm: 0,
        depthRadiusMm: 0,
        sampleStepMm: 0,
      },
    };

    if (!frame || !indexToWorld || mask.length !== frame.width * frame.rows.length) {
      return fallback;
    }

    const bounds = getChangedBounds(changed, frame, paddingPx);
    if (!bounds) return fallback;

    const model = getBoundaryModel();
    const out = new Map<string, VoxelIJKChange>();
    let projectedManualVoxelCount = 0;

    const isNearCurrentMask = (row: number, col: number) => {
      for (let yy = row - 1; yy <= row + 1; yy += 1) {
        for (let xx = col - 1; xx <= col + 1; xx += 1) {
          if (xx < 0 || yy < 0 || xx >= frame.width || yy >= frame.rows.length) {
            continue;
          }
          if (mask[yy * frame.width + xx] > 0) return true;
        }
      }
      return false;
    };

    for (const voxel of getManualModelAddedVoxelMap().values()) {
      const world = indexToWorld([voxel.i, voxel.j, voxel.k]);
      if (!world) continue;

      const row = findClosestBoundaryRowForWorld(world, frame);
      if (row < bounds.rowStart || row > bounds.rowEnd) continue;

      const rowFrame = frame.rows[row];
      if (!rowFrame) continue;

      const vx = world[0] - rowFrame.center[0];
      const vy = world[1] - rowFrame.center[1];
      const vz = world[2] - rowFrame.center[2];
      const uMm =
        vx * rowFrame.direction[0] +
        vy * rowFrame.direction[1] +
        vz * rowFrame.direction[2];
      const colFloat =
        (uMm / Math.max(1e-6, frame.widthMM) + 0.5) * frame.width - 0.5;
      const col = Math.round(colFloat);
      if (col < 0 || col >= frame.width) continue;

      projectedManualVoxelCount += 1;

      let shouldPrune = false;
      if (
        model &&
        row >= model.rowStart &&
        row <= model.rowEnd &&
        Number.isFinite(model.leftX[row]) &&
        Number.isFinite(model.rightX[row])
      ) {
        if (side === 'right') {
          shouldPrune = colFloat > model.rightX[row] + keepMarginPx;
        } else if (side === 'left') {
          shouldPrune = colFloat < model.leftX[row] - keepMarginPx;
        } else {
          shouldPrune =
            colFloat < model.leftX[row] - keepMarginPx ||
            colFloat > model.rightX[row] + keepMarginPx;
        }
      } else {
        shouldPrune = !isNearCurrentMask(row, col);
      }

      if (!shouldPrune) continue;
      out.set(getVoxelKey(voxel), { ...voxel, value: 255 });
    }

    return {
      voxelIJK: Array.from(out.values()),
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D: projectedManualVoxelCount,
      changedSeedCount2D: bounds.changedSeedCount2D,
      config: {
        method,
        radiusUMm: keepMarginPx,
        radiusVMm: 0,
        depthRadiusMm: 0,
        sampleStepMm: 0,
      },
    };
  };

  const collectSolidLumenVolumeVoxelIJK = ({
    mask,
    changed,
    value,
    method,
    paddingPx = 6,
    depthScale = 1.0,
    radialMarginMm = 0,
    edgeInsetPx = 0,
    leftEdgeInsetPx,
    rightEdgeInsetPx,
    rowNeighborSpread = 0,
    boundaryModel,
    clearMask,
  }: {
    mask: Uint8Array;
    changed: Iterable<number>;
    value: number;
    method: string;
    paddingPx?: number;
    depthScale?: number;
    radialMarginMm?: number;
    edgeInsetPx?: number;
    leftEdgeInsetPx?: number;
    rightEdgeInsetPx?: number;
    rowNeighborSpread?: number;
    boundaryModel?: CprBoundaryModel | null;
    clearMask?: Uint8Array | null;
  }): SolidLumenVolumeVoxelCollection => {
    const startedAt = performance.now();
    const frame = getBoundaryFrame();
    const worldToIndex = getLabelData()?.getWorldToIndex?.() as
      | ArrayLike<number>
      | undefined;
    const dims = getLabelData()?.getDimensions?.() as
      | [number, number, number]
      | undefined;
    const fallback: SolidLumenVolumeVoxelCollection = {
      voxelIJK: [],
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D: 0,
      changedSeedCount2D: 0,
      config: {
        method,
        radiusUMm: 0,
        radiusVMm: 0,
        depthRadiusMm: 0,
        sampleStepMm: 0,
      },
    };

    if (!frame || !worldToIndex || !dims || mask.length !== frame.width * frame.rows.length) {
      return fallback;
    }

    const bounds = getChangedBounds(changed, frame, paddingPx);
    if (!bounds) return fallback;

    const spacing = getImageSpacingMm(getLabelData());
    const minVoxelSpacingMm = Math.min(spacing[0], spacing[1], spacing[2]);
    const stepMm = clampValue(
      Math.min(minVoxelSpacingMm * 0.45, frame.spacingX * 0.45, frame.spacingY * 0.45),
      0.12,
      0.35,
    );
    const centerX = frame.width * 0.5;
    const out = new Map<string, VoxelIJKChange>();
    let sourcePointCount2D = 0;
    let maxRadiusUMm = 0;
    let maxDepthRadiusMm = 0;

    const pushVoxel = (world: Vec3) => {
      const voxel = worldPointToVoxelIJK(
        world,
        value,
        worldToIndex,
        dims,
        undefined,
        false,
      );
      if (!voxel) return;
      out.set(`${getVoxelKey(voxel)}:${value}`, voxel);
    };

    for (let row = bounds.rowStart; row <= bounds.rowEnd; row += 1) {
      const rowFrame = frame.rows[row];
      if (!rowFrame) continue;

      const modelRun =
        boundaryModel &&
        row >= boundaryModel.rowStart &&
        row <= boundaryModel.rowEnd &&
        Number.isFinite(boundaryModel.leftX[row]) &&
        Number.isFinite(boundaryModel.rightX[row])
          ? {
              left: boundaryModel.leftX[row],
              right: boundaryModel.rightX[row],
            }
          : null;
      const finalRun =
        modelRun ??
        findCenterRunInMaskRow(
          mask,
          frame.width,
          frame.rows.length,
          row,
          centerX,
        );
      const clearRun = clearMask
        ? findCenterRunInMaskRow(
            clearMask,
            frame.width,
            frame.rows.length,
            row,
            centerX,
          )
        : null;
      if (!finalRun && !clearRun) continue;

      let leftCol = finalRun?.left ?? clearRun!.left;
      let rightCol = finalRun?.right ?? clearRun!.right;
      const useModelEdges = !!modelRun && !clearRun;
      if (clearRun) {
        leftCol = Math.min(leftCol, clearRun.left);
        rightCol = Math.max(rightCol, clearRun.right);
      }

      const leftEdgeCoord = useModelEdges ? leftCol : leftCol;
      const rightEdgeCoord = useModelEdges ? rightCol : rightCol + 1.0;
      const runWidthPx = Math.max(1, rightEdgeCoord - leftEdgeCoord);
      const maxInset = Math.max(0, runWidthPx * 0.5 - 0.6);
      const effectiveLeftInsetPx = Math.max(
        0,
        Math.min(leftEdgeInsetPx ?? edgeInsetPx, maxInset),
      );
      const effectiveRightInsetPx = Math.max(
        0,
        Math.min(rightEdgeInsetPx ?? edgeInsetPx, maxInset),
      );
      const leftMM =
        (((leftEdgeCoord + effectiveLeftInsetPx) / Math.max(1, frame.width)) -
          0.5) *
          frame.widthMM -
        radialMarginMm;
      const rightMM =
        (((rightEdgeCoord - effectiveRightInsetPx) / Math.max(1, frame.width)) -
          0.5) *
          frame.widthMM +
        radialMarginMm;
      if (!(rightMM > leftMM + stepMm * 0.5)) {
        continue;
      }
      const leftRadiusMm = Math.max(stepMm * 0.75, -leftMM);
      const rightRadiusMm = Math.max(stepMm * 0.75, rightMM);
      const depthRadiusMm =
        Math.max(leftRadiusMm, rightRadiusMm, minVoxelSpacingMm * 0.85) *
        depthScale;
      const depthSteps = Math.max(1, Math.ceil(depthRadiusMm / stepMm));
      const vSteps = Math.max(0, Math.floor(rowNeighborSpread));
      const uAxis = normalizeVec3(rowFrame.direction, [1, 0, 0]);
      const vAxis = getBoundaryRowAxis(row, frame, rowFrame);
      const dAxis = normalizeVec3(crossVec3(uAxis, vAxis), [
        rowFrame.slabAxis?.[0] ?? 0,
        rowFrame.slabAxis?.[1] ?? 0,
        rowFrame.slabAxis?.[2] ?? 1,
      ]);

      maxRadiusUMm = Math.max(maxRadiusUMm, leftRadiusMm, rightRadiusMm);
      maxDepthRadiusMm = Math.max(maxDepthRadiusMm, depthRadiusMm);

      const colInsetExpand = Math.max(
        0,
        Math.ceil(Math.max(0, radialMarginMm) / Math.max(frame.spacingX, 1e-6)),
      );
      const colStart = Math.max(
        bounds.colStart,
        Math.floor(leftEdgeCoord + effectiveLeftInsetPx) - colInsetExpand,
      );
      const colEnd = Math.min(
        bounds.colEnd,
        Math.ceil(rightEdgeCoord - effectiveRightInsetPx) + colInsetExpand,
      );

      for (let col = colStart; col <= colEnd; col += 1) {
        const baseIdx = row * frame.width + col;
        if (!clearMask && mask[baseIdx] === 0) continue;
        sourcePointCount2D += clearMask || mask[baseIdx] > 0 ? 1 : 0;

        for (const uOffsetPx of [-0.35, 0, 0.35]) {
          const uMm =
            ((col + 0.5 + uOffsetPx) / Math.max(1, frame.width) - 0.5) *
            frame.widthMM;
          if (uMm < leftMM - 1e-6 || uMm > rightMM + 1e-6) continue;

          const sideRadiusMm = uMm < 0 ? leftRadiusMm : rightRadiusMm;
          const uNorm = Math.abs(uMm) / Math.max(stepMm, sideRadiusMm);
          if (uNorm > 1 + 1e-6) continue;
          const localDepthLimitMm = depthRadiusMm * Math.sqrt(Math.max(0, 1 - uNorm * uNorm));
          const localDepthSteps = Math.max(1, Math.min(depthSteps, Math.ceil(localDepthLimitMm / stepMm)));

          for (let vi = -vSteps; vi <= vSteps; vi += 1) {
            const vMm = vi * frame.spacingY * 0.35;
            for (let di = -localDepthSteps; di <= localDepthSteps; di += 1) {
              const depthMm = di * stepMm;
              if (Math.abs(depthMm) > localDepthLimitMm + 1e-6) continue;
              const world: Vec3 = [
                rowFrame.center[0] + uMm * uAxis[0] + vMm * vAxis[0] + depthMm * dAxis[0],
                rowFrame.center[1] + uMm * uAxis[1] + vMm * vAxis[1] + depthMm * dAxis[1],
                rowFrame.center[2] + uMm * uAxis[2] + vMm * vAxis[2] + depthMm * dAxis[2],
              ];
              pushVoxel(world);
            }
          }
        }
      }
    }

    return {
      voxelIJK: Array.from(out.values()),
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D,
      changedSeedCount2D: bounds.changedSeedCount2D,
      config: {
        method,
        radiusUMm: maxRadiusUMm,
        radiusVMm: maxRadiusUMm,
        depthRadiusMm: maxDepthRadiusMm,
        sampleStepMm: stepMm,
      },
    };
  };

  const collectConicalLumenDeltaVoxelIJK = ({
    mask,
    referenceMask,
    changed,
    value,
    method,
    side,
    boundaryModel,
    paddingPx = 5,
    depthScale = 0.5,
    maxDepthRadiusMm = 1.55,
    edgeInsetPx = 0.1,
    rootOverlapPx = 1.0,
  }: {
    mask: Uint8Array;
    referenceMask: Uint8Array | null | undefined;
    changed: Iterable<number>;
    value: number;
    method: string;
    side?: BoundarySide;
    boundaryModel?: CprBoundaryModel | null;
    paddingPx?: number;
    depthScale?: number;
    maxDepthRadiusMm?: number;
    edgeInsetPx?: number;
    rootOverlapPx?: number;
  }): SolidLumenVolumeVoxelCollection => {
    const startedAt = performance.now();
    const frame = getBoundaryFrame();
    const worldToIndex = getLabelData()?.getWorldToIndex?.() as
      | ArrayLike<number>
      | undefined;
    const dims = getLabelData()?.getDimensions?.() as
      | [number, number, number]
      | undefined;
    const fallback: SolidLumenVolumeVoxelCollection = {
      voxelIJK: [],
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D: 0,
      changedSeedCount2D: 0,
      config: {
        method,
        radiusUMm: 0,
        radiusVMm: 0,
        depthRadiusMm: 0,
        sampleStepMm: 0,
      },
    };

    if (
      !frame ||
      !worldToIndex ||
      !dims ||
      !referenceMask ||
      mask.length !== frame.width * frame.rows.length ||
      referenceMask.length !== mask.length
    ) {
      return fallback;
    }

    const changedSet = new Set<number>(Array.from(changed));
    const bounds = getChangedBounds(changedSet, frame, paddingPx);
    if (!bounds) return fallback;

    const spacing = getImageSpacingMm(getLabelData());
    const minVoxelSpacingMm = Math.min(spacing[0], spacing[1], spacing[2]);
    const stepMm = clampValue(
      Math.min(
        minVoxelSpacingMm * 0.45,
        frame.spacingX * 0.45,
        frame.spacingY * 0.45,
      ),
      0.12,
      0.35,
    );
    const centerX = frame.width * 0.5;
    const out = new Map<string, VoxelIJKChange>();
    let sourcePointCount2D = 0;
    let maxRadiusUMm = 0;
    let maxDepthMm = 0;

    const pushVoxel = (world: Vec3, weight?: number) => {
      const voxel = worldPointToVoxelIJK(
        world,
        value,
        worldToIndex,
        dims,
        weight,
        false,
      );
      if (!voxel) return;
      out.set(`${getVoxelKey(voxel)}:${value}`, voxel);
    };

    const getModelRun = (row: number) =>
      boundaryModel &&
      row >= boundaryModel.rowStart &&
      row <= boundaryModel.rowEnd &&
      Number.isFinite(boundaryModel.leftX[row]) &&
      Number.isFinite(boundaryModel.rightX[row])
        ? {
            left: boundaryModel.leftX[row],
            right: boundaryModel.rightX[row],
          }
        : null;

    const emitSide = (
      row: number,
      rowFrame: BoundaryFrame['rows'][number],
      activeSide: BoundarySide,
      baseRun: { left: number; right: number },
      finalRun: { left: number; right: number },
    ) => {
      const uAxis = normalizeVec3(rowFrame.direction, [1, 0, 0]);
      const vAxis = getBoundaryRowAxis(row, frame, rowFrame);
      const dAxis = normalizeVec3(crossVec3(uAxis, vAxis), [
        rowFrame.slabAxis?.[0] ?? 0,
        rowFrame.slabAxis?.[1] ?? 0,
        rowFrame.slabAxis?.[2] ?? 1,
      ]);

      const baseEdgePx =
        activeSide === 'right' ? baseRun.right + 0.5 : baseRun.left + 0.5;
      const finalEdgePx =
        activeSide === 'right' ? finalRun.right : finalRun.left;
      const deltaPx =
        value === 255
          ? activeSide === 'right'
            ? finalEdgePx - baseEdgePx
            : baseEdgePx - finalEdgePx
          : activeSide === 'right'
            ? baseEdgePx - finalEdgePx
            : finalEdgePx - baseEdgePx;
      if (!Number.isFinite(deltaPx) || deltaPx <= 0.25) return;

      const rowStart = row * frame.width;
      const extensionMm = Math.max(frame.spacingX * 0.5, deltaPx * frame.spacingX);
      const peakDepthMm = clampValue(
        extensionMm * depthScale,
        minVoxelSpacingMm * 0.35,
        maxDepthRadiusMm,
      );
      const depthSteps = Math.max(0, Math.ceil(peakDepthMm / stepMm));
      const rootOverlap = Math.max(0, rootOverlapPx);
      maxRadiusUMm = Math.max(maxRadiusUMm, extensionMm);
      maxDepthMm = Math.max(maxDepthMm, peakDepthMm);

      const colLo = Math.max(
        bounds.colStart,
        Math.floor(Math.min(baseEdgePx, finalEdgePx) - rootOverlap - edgeInsetPx) - 1,
      );
      const colHi = Math.min(
        bounds.colEnd,
        Math.ceil(Math.max(baseEdgePx, finalEdgePx) + rootOverlap + edgeInsetPx) + 1,
      );

      for (let col = colLo; col <= colHi; col += 1) {
        const idx = rowStart + col;
        if (idx < 0 || idx >= mask.length) continue;
        if (value === 255 && mask[idx] === 0) continue;
        if (value === 0 && referenceMask[idx] === 0) continue;

        for (const uOffsetPx of [-0.35, 0, 0.35]) {
          const uPx = col + 0.5 + uOffsetPx;
          const signedOutwardPx =
            value === 255
              ? activeSide === 'right'
                ? uPx - baseEdgePx
                : baseEdgePx - uPx
              : activeSide === 'right'
                ? baseEdgePx - uPx
                : uPx - baseEdgePx;
          if (
            signedOutwardPx < -rootOverlap - 0.1 ||
            signedOutwardPx > deltaPx + 0.1
          ) {
            continue;
          }

          const outwardT = clampValue(
            Math.max(0, signedOutwardPx) / Math.max(deltaPx, 1e-6),
            0,
            1,
          );
          const rootBlend =
            signedOutwardPx < 0
              ? clampValue(1 + signedOutwardPx / Math.max(rootOverlap, 1e-6), 0, 1)
              : 1;
          const uMm = (uPx / Math.max(1, frame.width) - 0.5) * frame.widthMM;
          const radialWeight = (1 - outwardT) * rootBlend;
          const localDepthLimitMm =
            peakDepthMm *
            Math.sqrt(Math.max(0, 1 - outwardT * outwardT)) *
            (0.42 + 0.58 * rootBlend);
          const localDepthSteps = Math.max(
            0,
            Math.min(depthSteps, Math.ceil(localDepthLimitMm / stepMm)),
          );

          for (let di = -localDepthSteps; di <= localDepthSteps; di += 1) {
            const depthMm = di * stepMm;
            if (Math.abs(depthMm) > localDepthLimitMm + 1e-6) continue;
            const depthT =
              localDepthLimitMm > 1e-6
                ? Math.abs(depthMm) / localDepthLimitMm
                : 0;
            const weight = Math.max(0.04, radialWeight * (1 - depthT * 0.55));
            const world: Vec3 = [
              rowFrame.center[0] + uMm * uAxis[0] + depthMm * dAxis[0],
              rowFrame.center[1] + uMm * uAxis[1] + depthMm * dAxis[1],
              rowFrame.center[2] + uMm * uAxis[2] + depthMm * dAxis[2],
            ];
            pushVoxel(world, Math.round(weight * 1000) / 1000);
          }
        }
        sourcePointCount2D += 1;
      }
    };

    for (let row = bounds.rowStart; row <= bounds.rowEnd; row += 1) {
      const rowFrame = frame.rows[row];
      if (!rowFrame) continue;

      const finalRun =
        getModelRun(row) ??
        findCenterRunInMaskRow(mask, frame.width, frame.rows.length, row, centerX);
      const baseRun = findCenterRunInMaskRow(
        referenceMask,
        frame.width,
        frame.rows.length,
        row,
        centerX,
      );
      if (!finalRun || !baseRun) continue;

      if (side === 'left' || !side) {
        emitSide(row, rowFrame, 'left', baseRun, finalRun);
      }
      if (side === 'right' || !side) {
        emitSide(row, rowFrame, 'right', baseRun, finalRun);
      }
    }

    return {
      voxelIJK: Array.from(out.values()),
      elapsedMs: performance.now() - startedAt,
      sourcePointCount2D,
      changedSeedCount2D: changedSet.size,
      config: {
        method,
        radiusUMm: maxRadiusUMm,
        radiusVMm: maxRadiusUMm,
        depthRadiusMm: maxDepthMm,
        sampleStepMm: stepMm,
      },
    };
  };


  return {
    collectManualAddedVoxelsOutsideCurrentBoundary,
    collectSolidLumenVolumeVoxelIJK,
    collectConicalLumenDeltaVoxelIJK,
  };
};
